import { useState } from 'react';
import {
  collection, doc, runTransaction,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { logActivity } from '../utils/activity';
import { Button, Modal, FormField } from './ui';
import { fmtMoney } from '../utils/currency';
import {
  buildEntry, postEntry, entryId,
  linesBankTransfer, linesBankDeposit, linesBankWithdrawal, linesBankCharge,
} from '../utils/ledger';
import type { BankAccount } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
function uid() { return `tg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`; }

export type BankMoveKind = 'transfer' | 'deposit' | 'withdrawal' | 'charge';

const TITLE: Record<BankMoveKind, string> = {
  transfer: 'Transfer Between Accounts',
  deposit: 'Deposit Cash',
  withdrawal: 'Withdraw Cash',
  charge: 'Bank Charge',
};

export default function BankMoveModal({ kind, accounts, defaultAccountId, onClose }: {
  kind: BankMoveKind;
  accounts: BankAccount[];
  defaultAccountId?: string;
  onClose: () => void;
}) {
  const { user, business } = useAuth();
  const first = defaultAccountId || (accounts.find(a => a.isDefault) ?? accounts[0])?.id || '';

  const [fromId, setFromId]   = useState(first);
  const [toId, setToId]       = useState('');
  const [amount, setAmount]   = useState('');
  const [date, setDate]       = useState(todayISO());
  const [description, setDescription] = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const isTransfer = kind === 'transfer';
  const from = accounts.find(a => a.id === fromId) ?? null;
  const to   = accounts.find(a => a.id === toId) ?? null;
  // deposit adds to a bank account; withdrawal + charge take from one.
  const primary = from;

  async function save() {
    const amt = parseFloat(amount);
    if (!fromId) return setError(isTransfer ? 'Choose the account to move money from.' : 'Choose an account.');
    if (isTransfer && !toId) return setError('Choose the account to move money into.');
    if (isTransfer && toId === fromId) return setError('Pick two different accounts.');
    if (isTransfer && from && to && from.currency !== to.currency) {
      return setError('Those accounts are in different currencies — record two separate entries for now.');
    }
    if (isNaN(amt) || amt <= 0) return setError('Enter a valid amount.');
    if (!date) return setError('Pick the date.');

    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business || !primary) { setError('Not connected.'); setSaving(false); return; }

    const nowISO = new Date().toISOString();
    try {
      await runTransaction(db, async tx => {
        if (isTransfer) {
          const fromRef = doc(db, 'bankAccounts', from!.id);
          const toRef   = doc(db, 'bankAccounts', to!.id);
          const fSnap = await tx.get(fromRef);
          const tSnap = await tx.get(toRef);
          if (!fSnap.exists() || !tSnap.exists()) throw new Error('An account no longer exists.');
          const groupId = uid();
          const outRef = doc(collection(db, 'bankTransactions'));
          const inRef  = doc(collection(db, 'bankTransactions'));
          const desc = description.trim() || `Transfer to ${to!.name}`;
          tx.set(outRef, stripUndef({
            businessId: business.id, accountId: from!.id, accountName: from!.name, currency: from!.currency,
            direction: 'out', amount: amt, date, description: desc,
            referenceType: 'transfer', transferGroupId: groupId, clearedStatus: 'uncleared',
            createdAt: nowISO, createdBy: user!.id,
          }));
          tx.set(inRef, stripUndef({
            businessId: business.id, accountId: to!.id, accountName: to!.name, currency: to!.currency,
            direction: 'in', amount: amt, date, description: description.trim() || `Transfer from ${from!.name}`,
            referenceType: 'transfer', transferGroupId: groupId, clearedStatus: 'uncleared',
            createdAt: nowISO, createdBy: user!.id,
          }));
          logActivity(tx, db, business.id, user, {
            module: 'bank', action: 'create', recordId: groupId,
            note: `Transfer: ${fmtMoney(amt, from!.currency)} from ${from!.name} to ${to!.name}`,
          });
          tx.update(fromRef, { currentBalance: (fSnap.data() as BankAccount).currentBalance - amt });
          tx.update(toRef,   { currentBalance: (tSnap.data() as BankAccount).currentBalance + amt });
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'bank_transfer', sourceId: groupId,
            sourceCollection: 'bankTransactions', date, currency: from!.currency, createdBy: user!.id,
            note: desc, lines: linesBankTransfer(amt, from!.id, to!.id),
          }), entryId('bank_transfer', groupId));
          return;
        }

        // deposit / withdrawal / charge — single leg on `primary`.
        const accRef = doc(db, 'bankAccounts', primary.id);
        const accSnap = await tx.get(accRef);
        if (!accSnap.exists()) throw new Error('That account no longer exists.');
        const bal = (accSnap.data() as BankAccount).currentBalance;
        const txRef = doc(collection(db, 'bankTransactions'));
        const dir = kind === 'deposit' ? 'in' : 'out';
        const refType = kind === 'deposit' ? 'deposit' : kind === 'withdrawal' ? 'withdrawal' : 'bank_charge';
        const desc = description.trim() || (kind === 'deposit' ? 'Cash deposit' : kind === 'withdrawal' ? 'Cash withdrawal' : 'Bank charge');
        tx.set(txRef, stripUndef({
          businessId: business.id, accountId: primary.id, accountName: primary.name, currency: primary.currency,
          direction: dir, amount: amt, date, description: desc,
          referenceType: refType, category: kind === 'charge' ? 'Bank Charges' : undefined,
          clearedStatus: 'uncleared', createdAt: nowISO, createdBy: user!.id,
        }));
        logActivity(tx, db, business.id, user, {
          module: 'bank', action: 'create', recordId: txRef.id,
          note: `${desc}: ${fmtMoney(amt, primary.currency)} · ${primary.name}`,
        });
        tx.update(accRef, { currentBalance: dir === 'in' ? bal + amt : bal - amt });
        const st = kind === 'deposit' ? 'bank_deposit' : kind === 'withdrawal' ? 'bank_withdrawal' : 'bank_charge';
        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: st, sourceId: txRef.id,
          sourceCollection: 'bankTransactions', date, currency: primary.currency, createdBy: user!.id,
          note: desc,
          lines: kind === 'deposit'    ? linesBankDeposit(amt, primary.id)
               : kind === 'withdrawal' ? linesBankWithdrawal(amt, primary.id)
               :                          linesBankCharge(amt, primary.id),
        }), entryId(st, txRef.id));
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} title={TITLE[kind]}
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>Save</Button>
      </>}>
      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="form-row-2">
        <FormField label={isTransfer ? 'From account' : kind === 'deposit' ? 'Into account' : 'Account'} required>
          <select value={fromId} onChange={e => setFromId(e.target.value)}>
            <option value="">Select…</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>)}
          </select>
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
      </div>

      {isTransfer && (
        <FormField label="To account" required>
          <select value={toId} onChange={e => setToId(e.target.value)}>
            <option value="">Select…</option>
            {accounts.filter(a => a.id !== fromId).map(a => <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>)}
          </select>
        </FormField>
      )}

      <div className="form-row-2">
        <FormField label={`Amount (${primary?.currency ?? ''})`} required>
          <input type="number" step="0.01" min="0.01" value={amount} className="no-spinner"
            onChange={e => setAmount(e.target.value)} />
        </FormField>
        <FormField label="Description (optional)">
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Reference…" />
        </FormField>
      </div>

      {kind === 'deposit' && <p className="form-hint">Records cash moving from your cash drawer into this bank account.</p>}
      {kind === 'withdrawal' && <p className="form-hint">Records cash taken out of this bank account into your cash drawer.</p>}
    </Modal>
  );
}
